// Consent persistence — first-party COOKIE (primary) + localStorage (mirror).
//
// The previous runtime stored consent in localStorage ONLY. localStorage can be
// partitioned/cleared independently of cookies and isn't shared the way a
// root-domain cookie is, so visitors were re-prompted on later visits. We now
// write a real cookie (Max-Age, Path=/, SameSite=Lax, Secure, registrable-root
// domain) and mirror it to localStorage for a fast, resilient read.

import type { ConsentRecord, RuntimeConfig } from './types';

/** Best-effort registrable root domain so the cookie covers apex + www. */
function rootDomain(): string | undefined {
  try {
    const host = location.hostname;
    // Don't set Domain for localhost / IPs — browsers reject it.
    if (host === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(host)) return undefined;
    const parts = host.split('.');
    if (parts.length <= 2) return host; // example.com
    // Take the last two labels (good enough for the common case; multi-part
    // TLDs like .co.uk fall back to the leaf domain, which still works — it just
    // won't share across an unusual subdomain split).
    return parts.slice(-2).join('.');
  } catch {
    return undefined;
  }
}

export function loadConsent(cfg: RuntimeConfig): ConsentRecord | null {
  const raw = readCookie(cfg.cookieName) ?? readLocal(cfg.cookieName);
  if (!raw) return null;
  try {
    const rec = JSON.parse(raw) as ConsentRecord;
    if (!rec || rec.v !== 2 || typeof rec.exp !== 'number') return null;
    if (Date.now() > rec.exp) return null; // expired → re-prompt
    if (rec.ver !== cfg.version) return null; // config version bumped → re-consent
    return rec;
  } catch {
    return null;
  }
}

export function saveConsent(cfg: RuntimeConfig, record: ConsentRecord): void {
  const value = JSON.stringify(record);
  const maxAge = Math.max(1, cfg.expiryDays) * 86400;
  const domain = rootDomain();
  const secure = location.protocol === 'https:' ? '; Secure' : '';
  const dom = domain ? `; Domain=${domain}` : '';
  try {
    document.cookie =
      `${encodeURIComponent(cfg.cookieName)}=${encodeURIComponent(value)}` +
      `; Max-Age=${maxAge}; Path=/; SameSite=Lax${secure}${dom}`;
  } catch {}
  try {
    localStorage.setItem(cfg.cookieName, value);
  } catch {}
}

export function clearConsent(cfg: RuntimeConfig): void {
  const domain = rootDomain();
  const dom = domain ? `; Domain=${domain}` : '';
  try {
    document.cookie = `${encodeURIComponent(cfg.cookieName)}=; Max-Age=0; Path=/${dom}`;
  } catch {}
  try {
    localStorage.removeItem(cfg.cookieName);
  } catch {}
}

function readCookie(name: string): string | null {
  try {
    const key = encodeURIComponent(name) + '=';
    const parts = document.cookie ? document.cookie.split('; ') : [];
    for (const p of parts) {
      if (p.indexOf(key) === 0) return decodeURIComponent(p.slice(key.length));
    }
  } catch {}
  return null;
}

function readLocal(name: string): string | null {
  try {
    return localStorage.getItem(name);
  } catch {
    return null;
  }
}
